import React, { useState, useEffect, useContext, useRef } from "react";
import Select from "react-select";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import "./CommercialOffer.css";
import { selectStyles } from "../../../../utils/selectStyles";
import {
  getSketchDesignCommercialOffer,
  getSketchDesignPaymentProcedures,
  updateSketchDesignCommercialOffer,
  addSketchDesignPaymentProcedure,
  updateSketchDesignPaymentProcedure,
  deleteSketchDesignPaymentProcedure,
  getSketchDesignApproval,
  createSketchDesignApproval,
  deleteSketchDesignApproval,
  updateSketchDesignApproverStatus,
} from "../../../../api/projectEstimatesApi";
import {
  getAllCounterparties,
  getCounterpartyBankAccounts,
  getImageServiceUrl,
} from "../../../../api/api";
import { getSketchDesignProjectById } from "../../../../api/projectApi";
import { getAPIURL } from "../../../../api/config";
import CommercialOfferApprovalModal from "../../ProjectFinance/CommercialOffer/CommercialOfferApprovalModal";
import AddCounterpartyModal from "../../CounterpartiesDatabase/AddCounterpartyModal/AddCounterpartyModal";

const CREATE_DESIGNER_OPTION = {
  value: "__create_new_designer__",
  label: "+ Создать компанию",
};
const CREATE_RECIPIENT_OPTION = {
  value: "__create_new_recipient__",
  label: "+ Создать контрагента",
};
const SIGNATURE_LAYOUT_STORAGE_KEY = "sketchCommercialOfferSignatureLayout";
const defaultSignatureLayout = {
  signature: { x: 6, y: 48, w: 190, h: 62 },
  seal: { x: 116, y: 6, w: 150, h: 150 },
};

// Генпроектировщики: short_company_name, при отсутствии — full_name, затем full_company_name
const getCounterpartyLabel = (c) =>
  c?.short_company_name || c?.full_name || c?.full_company_name || "";

// Получатели: short_company_name, при отсутствии — full_name, затем full_company_name
const getRecipientLabel = (c) =>
  c?.short_company_name || c?.full_name || c?.full_company_name || "";

// Нормализация id для совпадения в react-select (API может вернуть number или string)
const normalizeId = (id) => (id != null ? String(id) : null);
const getBlockAttachmentCoeff = (block) =>
  block?.attachment_coeff ?? block?.floor_coeff ?? 1;

const getApprovalLockMessage = (
  estimateOnApproval,
  commercialOfferOnApproval,
) => {
  if (estimateOnApproval && commercialOfferOnApproval) {
    return "Редактирование запрещено, так как смета и коммерческое предложение находятся на этапе согласования";
  }
  if (estimateOnApproval) {
    return "Редактирование запрещено, так как смета находится на этапе согласования";
  }
  if (commercialOfferOnApproval) {
    return "Редактирование запрещено, так как коммерческое предложение находится на этапе согласования";
  }
  return null;
};

const getBlockSpotId = (b) => b.spot_id ?? b.spot?.id ?? null;
const getBlockSpotName = (b) => b.spot?.name ?? null;

/** Группирует блоки по пятнам для КП. Возвращает { hasSpots, groups }. */
function groupBlocksBySpot(blocks) {
  if (!blocks || blocks.length === 0) return { hasSpots: false, groups: [] };
  const hasSpots = blocks.some((b) => getBlockSpotId(b) != null);
  if (!hasSpots)
    return {
      hasSpots: false,
      groups: [{ spotId: null, spotName: null, blocks }],
    };

  const bySpot = new Map();
  const spotsOrder = [];
  for (const block of blocks) {
    const sid = getBlockSpotId(block);
    const name = getBlockSpotName(block) ?? "—";
    if (!bySpot.has(sid)) {
      bySpot.set(sid, { spotId: sid, spotName: name, blocks: [] });
      spotsOrder.push(sid);
    }
    bySpot.get(sid).blocks.push(block);
  }
  const noSpot = bySpot.get(null) ?? bySpot.get(undefined);
  const groups = [];
  if (noSpot) groups.push(noSpot);
  for (const sid of spotsOrder) {
    if (sid != null && sid !== undefined) groups.push(bySpot.get(sid));
  }
  return { hasSpots: true, groups };
}

const CommercialOffer = ({
  projectId,
  projectLockedByApproval = false,
  estimateOnApproval = false,
  commercialOfferOnApproval = false,
  onApprovalStateChange,
}) => {
  const { userData } = useContext(UserContext);
  const isEditable = !projectLockedByApproval;
  const lockBannerMessage = getApprovalLockMessage(
    estimateOnApproval,
    commercialOfferOnApproval,
  );
  const [commercialData, setCommercialData] = useState(null);
  const [paymentProcedures, setPaymentProcedures] = useState([]);
  const [designersCounterparties, setDesignersCounterparties] = useState([]); // our_company — генпроектировщики
  const [recipientsCounterparties, setRecipientsCounterparties] = useState([]); // counterparty — получатели
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoErrors, setLogoErrors] = useState(new Set());
  const [loadingLogos, setLoadingLogos] = useState(new Set());
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [editingPercentageId, setEditingPercentageId] = useState(null);
  const [editingPercentageValue, setEditingPercentageValue] = useState("");
  const [paymentActionLoading, setPaymentActionLoading] = useState(false);
  const [saveOfferLoading, setSaveOfferLoading] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [workDeadlineDays, setWorkDeadlineDays] = useState(""); // срок в днях (для API deadline_days), пустая строка = не задано
  const [deadlineDescription, setDeadlineDescription] = useState(""); // текст срока выполнения (deadline_description)
  const [offerTitle, setOfferTitle] = useState(""); // поле title — текст под заголовком (вводная часть)
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const [isEditingDeadline, setIsEditingDeadline] = useState(false);
  const [editingDeadlineValue, setEditingDeadlineValue] = useState("");
  const [addPaymentForm, setAddPaymentForm] = useState({
    stage: "",
    percentage_ratio: "",
    deadline: "",
  });
  const [approvalData, setApprovalData] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isDeletingApproval, setIsDeletingApproval] = useState(false);
  const [isApproverActionLoading, setIsApproverActionLoading] = useState(false);
  const [showAddDesignerModal, setShowAddDesignerModal] = useState(false);
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
  const [ndsRate, setNdsRate] = useState(12);
  const [isSavingNdsRate, setIsSavingNdsRate] = useState(false);
  const [signatureLayout, setSignatureLayout] = useState(
    defaultSignatureLayout,
  );
  const [designerPrimaryBankAccount, setDesignerPrimaryBankAccount] =
    useState(null);
  const signatureDragRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(SIGNATURE_LAYOUT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.signature && parsed?.seal) {
          setSignatureLayout({
            signature: {
              ...defaultSignatureLayout.signature,
              ...parsed.signature,
            },
            seal: {
              ...defaultSignatureLayout.seal,
              ...parsed.seal,
            },
          });
        }
      }
    } catch (err) {
      console.warn("Не удалось прочитать настройки подписи/печати:", err);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        SIGNATURE_LAYOUT_STORAGE_KEY,
        JSON.stringify(signatureLayout),
      );
    } catch (err) {
      console.warn("Не удалось сохранить настройки подписи/печати:", err);
    }
  }, [signatureLayout]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const drag = signatureDragRef.current;
      if (!drag) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      setSignatureLayout((prev) => {
        const current = prev[drag.kind];
        if (!current) return prev;

        if (drag.mode === "move") {
          return {
            ...prev,
            [drag.kind]: {
              ...current,
              x: drag.origin.x + dx,
              y: drag.origin.y + dy,
            },
          };
        }

        return {
          ...prev,
          [drag.kind]: {
            ...current,
            w: Math.max(24, drag.origin.w + dx),
            h: Math.max(24, drag.origin.h + dy),
          },
        };
      });
    };

    const handleMouseUp = () => {
      signatureDragRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const startSignatureDrag = (kind, mode) => (e) => {
    if (!isEditable) return;
    e.preventDefault();
    e.stopPropagation();
    signatureDragRef.current = {
      kind,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origin: { ...signatureLayout[kind] },
    };
  };

  const refetchPaymentAndOffer = async () => {
    if (!projectId) return;
    try {
      const offer = await getSketchDesignCommercialOffer(projectId);
      setCommercialData(offer);
      const procedures = await getSketchDesignPaymentProcedures(projectId);
      setPaymentProcedures(procedures || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveOfferParams = async (overrides = {}) => {
    if (!isEditable) return;
    if (!projectId) return;
    const designerId = overrides.designerId ?? selectedDesigner?.value;
    const recipientId = overrides.recipientId ?? selectedRecipient?.value;
    setSaveOfferLoading(true);
    try {
      const payload = {};
      if (designerId) {
        payload.counterparty_id = Number(designerId);
      }
      if (recipientId) {
        payload.receiver_id = Number(recipientId);
      }
      const days = workDeadlineDays !== "" ? Number(workDeadlineDays) : null;
      if (days != null && !Number.isNaN(days) && days >= 0) {
        payload.deadline_days = days;
      }
      if (deadlineDescription.trim()) {
        payload.deadline_description = deadlineDescription.trim();
      }
      if (offerTitle.trim()) {
        payload.title = offerTitle.trim();
      }
      payload.nds_percent = ndsRate;
      const updated = await updateSketchDesignCommercialOffer(
        projectId,
        payload,
      );
      setCommercialData(updated);
      if (updated?.deadline_days != null)
        setWorkDeadlineDays(updated.deadline_days);
      setDeadlineDescription(updated?.deadline_description ?? "");
      setOfferTitle(updated?.title ?? "");
      if (updated?.current_nds != null) setNdsRate(updated.current_nds);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (err) {
      console.error(err);
      alert(err?.message || "Ошибка сохранения");
    } finally {
      setSaveOfferLoading(false);
    }
  };

  // Загрузка данных коммерческого предложения и компаний
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        console.log("projectId не передан:", projectId);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log("Загружаем данные для проекта ЭП:", projectId);

        // Сначала запрашиваем КП, чтобы бэкенд успел пересчитать его (в т.ч. после изменения блоков).
        // Этапы оплаты зависят от актуального КП — запрашиваем их после.
        const commercialDataResponse =
          await getSketchDesignCommercialOffer(projectId);
        setCommercialData(commercialDataResponse);

        const [
          paymentProceduresResponse,
          ourCompanies,
          counterpartiesList,
          projectDataResponse,
          approvalResponse,
        ] = await Promise.all([
          getSketchDesignPaymentProcedures(projectId),
          getAllCounterparties({ counterparty_side: "our_company" }),
          getAllCounterparties({ counterparty_side: "counterparty" }),
          getSketchDesignProjectById(projectId),
          getSketchDesignApproval(projectId, "commercial_offer"),
        ]);

        setPaymentProcedures(paymentProceduresResponse || []);
        setWorkDeadlineDays(
          commercialDataResponse?.deadline_days != null
            ? commercialDataResponse.deadline_days
            : "",
        );
        setDeadlineDescription(
          commercialDataResponse?.deadline_description ?? "",
        );
        setOfferTitle(commercialDataResponse?.title ?? "");
        if (commercialDataResponse?.current_nds != null)
          setNdsRate(commercialDataResponse.current_nds);
        setDesignersCounterparties(ourCompanies || []);
        setRecipientsCounterparties(counterpartiesList || []);
        setProjectData(projectDataResponse);
        setApprovalData(approvalResponse ?? null);

        // Генпроектировщик: из КП (counterparty_id / counterparty) или первый из наших компаний
        const counterpartyFromOffer = commercialDataResponse?.counterparty;
        const counterpartyIdFromOffer = commercialDataResponse?.counterparty_id;
        if (ourCompanies?.length > 0) {
          const designer =
            counterpartyIdFromOffer != null
              ? ourCompanies.find(
                  (c) =>
                    normalizeId(c.id) === normalizeId(counterpartyIdFromOffer),
                )
              : counterpartyFromOffer?.id != null
                ? ourCompanies.find(
                    (c) =>
                      normalizeId(c.id) ===
                      normalizeId(counterpartyFromOffer.id),
                  )
                : null;
          const designerToSelect = designer || ourCompanies[0];
          setSelectedDesigner({
            value: normalizeId(designerToSelect.id),
            label: getCounterpartyLabel(designerToSelect),
          });
        }

        // Получатель: из КП (receiver_id / receiver) или первый из контрагентов
        const receiverFromOffer = commercialDataResponse?.receiver;
        const receiverIdFromOffer = commercialDataResponse?.receiver_id;
        if (counterpartiesList?.length > 0) {
          const recipient =
            receiverIdFromOffer != null
              ? counterpartiesList.find(
                  (c) => normalizeId(c.id) === normalizeId(receiverIdFromOffer),
                )
              : receiverFromOffer?.id != null
                ? counterpartiesList.find(
                    (c) =>
                      normalizeId(c.id) === normalizeId(receiverFromOffer.id),
                  )
                : null;
          const recipientToSelect = recipient || counterpartiesList[0];
          setSelectedRecipient({
            value: normalizeId(recipientToSelect.id),
            label: getRecipientLabel(recipientToSelect),
          });
        }
      } catch (err) {
        console.error("Ошибка при загрузке данных:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  useEffect(() => {
    const counterpartyId = selectedDesigner?.value;
    if (!counterpartyId) {
      setDesignerPrimaryBankAccount(null);
      return undefined;
    }

    let cancelled = false;

    const loadBankAccount = async () => {
      try {
        const accounts = await getCounterpartyBankAccounts(
          Number(counterpartyId),
        );
        if (cancelled) return;

        const list = Array.isArray(accounts) ? accounts : [];
        const primary = list.find((acc) => acc.is_primary) || list[0] || null;
        setDesignerPrimaryBankAccount(primary);
      } catch (err) {
        console.error("Ошибка при загрузке банковских счетов:", err);
        if (!cancelled) setDesignerPrimaryBankAccount(null);
      }
    };

    loadBankAccount();

    return () => {
      cancelled = true;
    };
  }, [selectedDesigner?.value]);

  const getStatusIcon = (status) => {
    if (!status)
      return (
        <span className="commercial-offer-approval-status-icon pending">
          ⏳
        </span>
      );
    const s = String(status).toLowerCase();
    if (s === "согласован" || s === "approved")
      return (
        <span className="commercial-offer-approval-status-icon approved">
          ✓
        </span>
      );
    if (s === "на рассмотрении" || s === "pending")
      return (
        <span className="commercial-offer-approval-status-icon pending">
          ⏳
        </span>
      );
    if (s === "не согласован" || s === "rejected")
      return (
        <span className="commercial-offer-approval-status-icon rejected">
          ✕
        </span>
      );
    return (
      <span className="commercial-offer-approval-status-icon pending">⏳</span>
    );
  };

  const getStatusText = (status) => {
    if (!status) return "На рассмотрении";
    const s = String(status).toLowerCase();
    if (s === "согласован" || s === "approved") return "Согласован";
    if (s === "на рассмотрении" || s === "pending") return "На рассмотрении";
    if (s === "не согласован" || s === "rejected") return "Отклонен";
    return "На рассмотрении";
  };

  const toNumericApproverIds = (checked) =>
    checked
      .map((id) => (typeof id === "number" ? id : parseInt(id, 10)))
      .filter((id) => Number.isInteger(id) && !Number.isNaN(id));

  const handleApprovalSubmit = async (checkedIds) => {
    const ids = toNumericApproverIds(checkedIds);
    if (!projectId || ids.length === 0) return;
    setIsSubmittingApproval(true);
    try {
      await createSketchDesignApproval(projectId, "commercial_offer", ids);
      const updated = await getSketchDesignApproval(
        projectId,
        "commercial_offer",
      );
      setApprovalData(updated ?? null);
      setShowApprovalModal(false);
      onApprovalStateChange?.();
      alert("Коммерческое предложение успешно отправлено на согласование.");
    } catch (err) {
      console.error("Ошибка при отправке на согласование:", err);
      alert(err?.message || "Ошибка при отправке на согласование.");
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const handleDeleteApproval = async () => {
    if (
      !projectId ||
      !window.confirm("Удалить согласование по коммерческому предложению?")
    )
      return;
    setIsDeletingApproval(true);
    try {
      await deleteSketchDesignApproval(projectId, "commercial_offer");
      setApprovalData(null);
      onApprovalStateChange?.();
      alert("Согласование удалено.");
    } catch (err) {
      console.error("Ошибка при удалении согласования:", err);
      alert(err?.message || "Ошибка при удалении согласования.");
    } finally {
      setIsDeletingApproval(false);
    }
  };

  const myApprover = approvalData?.approvers?.find(
    (a) =>
      a.user?.id === userData?.id || String(a.user_id) === String(userData?.id),
  );
  const isMyApprovalPending =
    myApprover &&
    ["in_processing", "pending", "на рассмотрении"].includes(
      String(myApprover.status || "").toLowerCase(),
    );

  const handlePrintPdf = () => {
    const source = document.querySelector(".commercial-offer-document");
    if (!source) {
      alert("Не найден блок коммерческого предложения для печати.");
      return;
    }

    const styleNodes = Array.from(document.querySelectorAll("style"))
      .map((node) => node.outerHTML)
      .join("\n");
    const linkNodes = Array.from(
      document.querySelectorAll('link[rel="stylesheet"]'),
    )
      .map((node) => node.outerHTML)
      .join("\n");

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "210mm";
    iframe.style.height = "297mm";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const printDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!printDoc) {
      alert("Не удалось подготовить документ к печати.");
      iframe.remove();
      return;
    }

    printDoc.open();
    printDoc.write(`
      <!doctype html>
      <html lang="ru">
        <head>
          <meta charset="utf-8" />
          <title></title>
          ${linkNodes}
          ${styleNodes}
          <style>
            @page { size: A4; margin: 8mm; }
            html,
            body {
              margin: 0;
              padding: 0;
              width: 100%;
              box-sizing: border-box;
              background: #fff !important;
              font-size: 11.5px;
              line-height: 1.25;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .commercial-offer-document {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: none !important;
              box-sizing: border-box !important;
              box-shadow: none !important;
              border: none !important;
              font-family: 'Times New Roman', serif !important;
              font-size: 11.5px !important;
              line-height: 1.25 !important;
            }
            .commercial-offer-document .work-table,
            .commercial-offer-document .payment-table,
            .commercial-offer-document .document-bottom,
            .commercial-offer-document .document-content,
            .commercial-offer-document .document-footer {
              width: 100% !important;
              max-width: none !important;
              box-sizing: border-box !important;
            }
            .commercial-offer-document * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .commercial-offer-document h1,
            .commercial-offer-document h2,
            .commercial-offer-document h3,
            .commercial-offer-document h4 {
              font-size: 1.15em !important;
              line-height: 1.2 !important;
              margin-top: 0 !important;
            }
            .commercial-offer-document p,
            .commercial-offer-document div,
            .commercial-offer-document span,
            .commercial-offer-document label {
              font-size: 1em !important;
            }
            .commercial-offer-document .document-header {
              display: flex !important;
              flex-direction: row !important;
              flex-wrap: nowrap !important;
              justify-content: center !important;
              align-items: flex-start !important;
              gap: 12px !important;
              margin-bottom: 8px !important;
              width: 100% !important;
            }
            .commercial-offer-document .document-header .sender-info-latin,
            .commercial-offer-document .document-header .sender-info-cyrillic {
              flex: 1 1 0 !important;
              max-width: none !important;
              width: auto !important;
            }
            .commercial-offer-document .document-header .sender-info-latin {
              text-align: right !important;
            }
            .commercial-offer-document .document-header .sender-info-cyrillic {
              text-align: left !important;
            }
            .commercial-offer-document .document-header .company-name {
              font-weight: bold !important;
              margin-bottom: 2px !important;
            }
            .commercial-offer-document .document-header .company-address,
            .commercial-offer-document .document-header .company-address-ru,
            .commercial-offer-document .document-header .company-details {
              margin-bottom: 2px !important;
              line-height: 1.25 !important;
            }
            .commercial-offer-document .document-header .company-details div {
              margin-bottom: 1px !important;
            }
            .commercial-offer-document .document-header .document-logo {
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              flex: 0 0 120px !important;
              width: 120px !important;
              min-width: 120px !important;
              margin: 0 !important;
              min-height: 48px !important;
            }
            .commercial-offer-document .document-header .document-logo img {
              height: 48px !important;
              max-width: 120px !important;
              padding: 0 !important;
            }
            .commercial-offer-document .document-bottom {
              margin-bottom: 6px !important;
              padding-top: 8px !important;
              border-top: 1px solid #e0e0e0 !important;
            }
            .commercial-offer-document .document-title {
              margin-bottom: 8px !important;
            }
            .commercial-offer-document .document-content {
              margin-bottom: 8px !important;
            }
            .commercial-offer-document .document-content p,
            .commercial-offer-document .document-editable-display p {
              margin-bottom: 4px !important;
              line-height: 1.25 !important;
            }
            .commercial-offer-document .document-title-block {
              margin-bottom: 6px !important;
            }
            .commercial-offer-document .document-cost {
              margin-top: 0 !important;
            }
            .commercial-offer-document .work-table {
              margin-bottom: 16px !important;
            }
            .commercial-offer-document .payment-table {
              margin-bottom: 8px !important;
            }
            .commercial-offer-document .work-table h3,
            .commercial-offer-document .payment-table h3 {
              margin-bottom: 3px !important;
              font-size: 1.05em !important;
            }
            .commercial-offer-document .work-table table,
            .commercial-offer-document .payment-table table {
              table-layout: fixed !important;
              width: 100% !important;
              font-size: 11.5px !important;
            }
            .commercial-offer-document .work-table th,
            .commercial-offer-document .work-table td,
            .commercial-offer-document .payment-table table th,
            .commercial-offer-document .payment-table table td {
              padding: 2px 3px !important;
              font-family: inherit !important;
              font-size: 11.5px !important;
              line-height: 1.25 !important;
              min-width: 0 !important;
              white-space: normal !important;
              word-break: break-word !important;
            }
            .commercial-offer-document .work-table th:nth-child(1),
            .commercial-offer-document .work-table td:nth-child(1),
            .commercial-offer-document .payment-table table th:nth-child(1),
            .commercial-offer-document .payment-table table td:nth-child(1) {
              width: 22px !important;
              max-width: 22px !important;
              text-align: center !important;
              white-space: nowrap !important;
              padding-left: 1px !important;
              padding-right: 1px !important;
            }
            .commercial-offer-document .work-table th:nth-child(2),
            .commercial-offer-document .work-table td:nth-child(2),
            .commercial-offer-document .payment-table table th:nth-child(2),
            .commercial-offer-document .payment-table table td:nth-child(2) {
              width: 34% !important;
              text-align: left !important;
            }
            .commercial-offer-document .work-table th:nth-child(3),
            .commercial-offer-document .work-table td:nth-child(3),
            .commercial-offer-document .work-table th:nth-child(4),
            .commercial-offer-document .work-table td:nth-child(4),
            .commercial-offer-document .work-table th:nth-child(5),
            .commercial-offer-document .work-table td:nth-child(5),
            .commercial-offer-document .work-table th:nth-child(6),
            .commercial-offer-document .work-table td:nth-child(6),
            .commercial-offer-document .payment-table table th:nth-child(3),
            .commercial-offer-document .payment-table table td:nth-child(3),
            .commercial-offer-document .payment-table table th:nth-child(4),
            .commercial-offer-document .payment-table table td:nth-child(4),
            .commercial-offer-document .payment-table table th:nth-child(5),
            .commercial-offer-document .payment-table table td:nth-child(5) {
              min-width: 0 !important;
            }
            .commercial-offer-document .work-table .work-table-spot-header td {
              width: auto !important;
              max-width: none !important;
              text-align: left !important;
            }
            .commercial-offer-document .work-table .work-table-spot-header td,
            .commercial-offer-document .work-table .work-table-spot-total td,
            .commercial-offer-document .work-table .nds-row td,
            .commercial-offer-document .work-table .grand-total-with-nds td,
            .commercial-offer-document .work-table .summary-row td,
            .commercial-offer-document .work-table .section-total td {
              padding: 2px 3px !important;
              font-family: inherit !important;
              font-size: 11.5px !important;
              line-height: 1.25 !important;
            }
            .commercial-offer-document .additional-info {
              margin-bottom: 8px !important;
            }
            .commercial-offer-document .info-section {
              margin-bottom: 4px !important;
            }
            .commercial-offer-document .info-section h4,
            .commercial-offer-document .info-section p,
            .commercial-offer-document .work-deadline {
              margin-bottom: 2px !important;
              line-height: 1.25 !important;
            }
            .commercial-offer-document .btn-add-payment,
            .commercial-offer-document .edit-icon-btn,
            .commercial-offer-document .delete-payment-btn,
            .commercial-offer-document .commercial-offer-actions,
            .commercial-offer-document .commercial-offer-approver-actions,
            .commercial-offer-document .add-counterparty-modal,
            .commercial-offer-document .add-counterparty-modal-backdrop {
              display: none !important;
            }
            .commercial-offer-document .payment-table button,
            .commercial-offer-document .payment-table input,
            .commercial-offer-document .payment-table select,
            .commercial-offer-document .work-deadline button,
            .commercial-offer-document .work-deadline input {
              display: none !important;
            }
            .commercial-offer-document .work-deadline-empty {
              display: none !important;
            }
            .commercial-offer-document .payment-table {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .commercial-offer-document .payment-table.payment-table-empty {
              display: none !important;
            }
            .commercial-offer-document .payment-table-header {
              break-after: avoid;
              page-break-after: avoid;
              margin-bottom: 2px !important;
            }
            .commercial-offer-document .payment-table table {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .commercial-offer-document .payment-table table th:last-child,
            .commercial-offer-document .payment-table table td:last-child {
              display: none !important;
            }
            .commercial-offer-document .document-footer {
              display: flex !important;
              justify-content: space-between !important;
              align-items: flex-start !important;
              gap: 12px !important;
              margin-top: 60px !important;
            }
            .commercial-offer-document .document-footer-requisites {
              flex: 1 1 58% !important;
            }
            .commercial-offer-document .document-footer-requisites p {
              font-family: inherit !important;
              font-size: 1em !important;
              line-height: 1.25 !important;
              margin: 0 !important;
            }
            .commercial-offer-document .document-footer-signatures {
              flex: 1 1 42% !important;
              text-align: right !important;
              display: flex !important;
              flex-direction: column !important;
              align-items: flex-end !important;
              justify-content: flex-start !important;
            }
            .commercial-offer-document .document-footer-signatures .signatures {
              align-items: flex-end !important;
              margin-top: 0 !important;
            }
            .commercial-offer-document .document-footer-requisites h4 {
              margin-top: 0 !important;
              margin-bottom: 2px !important;
            }
            .commercial-offer-document .document-footer-signatures .signatures-regards {
              margin-top: 0 !important;
              margin-bottom: 2px !important;
            }
            .commercial-offer-document .document-footer-signatures .signature-block {
              margin-top: 0 !important;
              margin-bottom: 2px !important;
              min-height: 0 !important;
              padding-top: 0 !important;
            }
            .commercial-offer-document .document-footer-signatures .signature-images {
              width: 280px !important;
              height: 90px !important;
            }
          </style>
        </head>
        <body>
          ${source.outerHTML}
        </body>
      </html>
    `);
    printDoc.close();

    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      alert("Не удалось открыть печать.");
      iframe.remove();
      return;
    }

    const cleanup = () => {
      setTimeout(() => {
        iframe.remove();
      }, 300);
    };

    frameWindow.addEventListener("afterprint", cleanup, { once: true });
    setTimeout(() => {
      frameWindow.focus();
      frameWindow.print();
    }, 120);
  };

  const handleApproverSetStatus = async (status) => {
    if (!myApprover?.id || !projectId) return;
    setIsApproverActionLoading(true);
    try {
      await updateSketchDesignApproverStatus(myApprover.id, status);
      const updated = await getSketchDesignApproval(
        projectId,
        "commercial_offer",
      );
      setApprovalData(updated ?? null);
      alert(
        status === "approved"
          ? "Коммерческое предложение согласовано."
          : "Коммерческое предложение отклонено.",
      );
    } catch (err) {
      console.error("Ошибка при обновлении статуса:", err);
      alert(err?.message || "Ошибка при обновлении статуса");
    } finally {
      setIsApproverActionLoading(false);
    }
  };

  // Выбранный контрагент — генпроектировщик (наша компания)
  const selectedDesignerCounterparty = selectedDesigner
    ? designersCounterparties.find(
        (c) => normalizeId(c.id) === selectedDesigner.value,
      )
    : designersCounterparties[0];

  // Выбранный контрагент — получатель
  const selectedRecipientCounterparty = selectedRecipient
    ? recipientsCounterparties.find(
        (c) => normalizeId(c.id) === selectedRecipient.value,
      )
    : recipientsCounterparties[0];

  const formatCounterpartyAddress = (c) => {
    if (!c) return "";
    const parts = [
      c.postal_code,
      c.country,
      c.city,
      c.street,
      c.building,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Функция для формирования URL логотипа компании (если у контрагента есть logo_url)
  const getCompanyLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;

    let imageUrl = logoUrl;

    if (!imageUrl.startsWith("http")) {
      if (imageUrl.startsWith("/")) {
        imageUrl = `${getAPIURL().replace(/\/$/, "")}${imageUrl}`;
      } else {
        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";

        if (isLocalhost) {
          imageUrl = `${getAPIURL()}images/company_logos/${imageUrl}`;
        } else {
          imageUrl = `${getAPIURL()}images/company_logos/${imageUrl}`;
        }
      }
    }

    const finalUrl = decodeURIComponent(imageUrl);
    return finalUrl;
  };

  // Обработчик ошибки загрузки логотипа
  const handleLogoError = (companyId) => {
    setLogoErrors((prev) => new Set([...prev, companyId]));
  };

  const designerOptions = [
    CREATE_DESIGNER_OPTION,
    ...designersCounterparties.map((c) => ({
      value: normalizeId(c.id),
      label: getCounterpartyLabel(c),
    })),
  ];

  const recipientOptions = [
    CREATE_RECIPIENT_OPTION,
    ...recipientsCounterparties.map((c) => ({
      value: normalizeId(c.id),
      label: getRecipientLabel(c),
    })),
  ];

  const signatureName =
    commercialData?.signature_name ||
    selectedDesignerCounterparty?.full_name_first_supervisor ||
    selectedDesignerCounterparty?.full_name;
  const deadlineDisplayText =
    deadlineDescription ||
    commercialData?.deadline_description ||
    commercialData?.deadline ||
    (workDeadlineDays !== "" && !Number.isNaN(Number(workDeadlineDays))
      ? `${workDeadlineDays} дн.` +
        (Number(workDeadlineDays) >= 30
          ? ` (≈ ${Math.round(Number(workDeadlineDays) / 30)} мес.)`
          : "")
      : "—");

  // Сумма процентов по этапам оплаты (константа 100%)
  const totalPaymentPercent = paymentProcedures.reduce(
    (sum, p) => sum + (Number(p.percentage_ratio) || 0),
    0,
  );
  const remainingPercent = Math.max(0, 100 - totalPaymentPercent);

  // Проверяем, передан ли projectId
  if (!projectId) {
    return (
      <div className="commercial-offer-container">
        <div className="error">Ошибка: ID проекта не передан</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="commercial-offer-container">
        <div className="loading">Загрузка данных...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="commercial-offer-container">
        <div className="error">Ошибка: {error}</div>
      </div>
    );
  }

  return (
    <div className="commercial-offer-container">
      {projectLockedByApproval && lockBannerMessage && (
        <div className="commercial-offer-lock-banner" role="alert">
          <span className="commercial-offer-lock-banner-icon">⚠</span>
          {lockBannerMessage}
        </div>
      )}
      {/* Параметры коммерческого предложения */}
      <div className="commercial-offer-params">
        <div
          className="commercial-offer-top-bar"
          style={{ flexWrap: "wrap", gap: "12px" }}
        >
          <h2 className="commercial-offer-title">
            Параметры коммерческого предложения
          </h2>
          {approvalData?.approvers?.length > 0 ? (
            <div className="commercial-offer-approval-with-actions">
              <div className="commercial-offer-approval-status-block">
                {approvalData.approvers.map((approver, index) => {
                  const name = approver.user
                    ? [approver.user.surname, approver.user.name]
                        .filter(Boolean)
                        .join(" ")
                        .trim()
                    : "Не указан";
                  return (
                    <div
                      key={approver.id ?? index}
                      className="commercial-offer-approval-step"
                    >
                      <span className="commercial-offer-approver-name">
                        {name || "Не указан"}:
                      </span>
                      {getStatusIcon(approver.status)}
                      <span className="commercial-offer-step-status-text">
                        {getStatusText(approver.status)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="commercial-offer-btn-delete-approval"
                onClick={handleDeleteApproval}
                disabled={isDeletingApproval}
              >
                {isDeletingApproval ? "Удаление..." : "Удалить согласование"}
              </button>
            </div>
          ) : (
            <div className="commercial-offer-approval-empty-row">
              <div className="commercial-offer-approval-empty">
                <span className="commercial-offer-approval-empty-icon">ℹ️</span>
                Согласований по коммерческому предложению пока нет
              </div>
              <button
                type="button"
                className="commercial-offer-btn-send-approval"
                onClick={() => setShowApprovalModal(true)}
                disabled={isSubmittingApproval}
              >
                {isSubmittingApproval
                  ? "Отправка..."
                  : "Отправить на согласование"}
              </button>
            </div>
          )}
        </div>

        <div className="params-fields">
          <div className="param-field">
            <label>Выберите генпроектировщика*</label>
            <Select
              styles={selectStyles}
              options={designerOptions}
              value={selectedDesigner}
              isDisabled={!isEditable}
              onChange={(val) => {
                if (val?.value === CREATE_DESIGNER_OPTION.value) {
                  setShowAddDesignerModal(true);
                  return;
                }
                setSelectedDesigner(val);
                if (val?.value && selectedRecipient?.value) {
                  handleSaveOfferParams({
                    designerId: val.value,
                    recipientId: selectedRecipient.value,
                  });
                }
              }}
              placeholder="Выберите проектировщика..."
              formatOptionLabel={(option) =>
                option.value === CREATE_DESIGNER_OPTION.value ? (
                  <span style={{ color: "#2a5e38", fontWeight: 500 }}>
                    {option.label}
                  </span>
                ) : (
                  option.label
                )
              }
            />
          </div>

          <div className="param-field">
            <label>Выберите компанию-получателя*</label>
            <Select
              styles={selectStyles}
              options={recipientOptions}
              value={selectedRecipient}
              isDisabled={!isEditable}
              onChange={(val) => {
                if (val?.value === CREATE_RECIPIENT_OPTION.value) {
                  setShowAddRecipientModal(true);
                  return;
                }
                setSelectedRecipient(val);
                if (val?.value && selectedDesigner?.value) {
                  handleSaveOfferParams({
                    designerId: selectedDesigner.value,
                    recipientId: val.value,
                  });
                }
              }}
              placeholder="Выберите получателя..."
              formatOptionLabel={(option) =>
                option.value === CREATE_RECIPIENT_OPTION.value ? (
                  <span style={{ color: "#2a5e38", fontWeight: 500 }}>
                    {option.label}
                  </span>
                ) : (
                  option.label
                )
              }
            />
          </div>
        </div>

        <div className="params-fields-second-row">
          <div className="param-field">
            <label>Ставка НДС (%)</label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="number"
                value={ndsRate}
                onChange={(e) => setNdsRate(parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                step="1"
                disabled={!isEditable || isSavingNdsRate}
                style={{
                  width: "100px",
                  padding: "8px 12px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <button
                onClick={async () => {
                  if (!projectId || !isEditable) return;
                  setIsSavingNdsRate(true);
                  try {
                    const updated = await updateSketchDesignCommercialOffer(
                      projectId,
                      { nds_percent: ndsRate },
                    );
                    setCommercialData(updated);
                    if (updated?.current_nds != null)
                      setNdsRate(updated.current_nds);
                    setShowSaveSuccess(true);
                    setTimeout(() => setShowSaveSuccess(false), 3000);
                  } catch (err) {
                    console.error("Ошибка при сохранении ставки НДС:", err);
                    alert(err?.message || "Ошибка сохранения ставки НДС");
                  } finally {
                    setIsSavingNdsRate(false);
                  }
                }}
                disabled={!isEditable || isSavingNdsRate}
                style={{
                  padding: "8px 16px",
                  background: "#2e7d32",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor:
                    !isEditable || isSavingNdsRate ? "not-allowed" : "pointer",
                  opacity: !isEditable || isSavingNdsRate ? 0.6 : 1,
                }}
              >
                {isSavingNdsRate ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </div>
        </div>

        <div className="commercial-offer-links">
          <h3 className="offer-title">Коммерческое предложение</h3>
        </div>
      </div>

      {/* Документ коммерческого предложения */}
      <div className="commercial-offer-document">
        <div className="document-header">
          <div className="sender-info-latin">
            <div className="company-name">
              {getCounterpartyLabel(selectedDesignerCounterparty)}
            </div>
            <div className="company-address">
              {formatCounterpartyAddress(selectedDesignerCounterparty)}
            </div>
            <div className="company-details">
              <div>БИН/ИИН: {selectedDesignerCounterparty?.bin_or_inn}</div>
              <div>тел: {selectedDesignerCounterparty?.phone}</div>
              <div>e-mail: {selectedDesignerCounterparty?.email}</div>
            </div>
          </div>

          <div className="document-logo">
            {selectedDesignerCounterparty?.logo_url &&
              getCompanyLogoUrl(selectedDesignerCounterparty.logo_url) &&
              !logoErrors.has(selectedDesignerCounterparty.id) && (
                <div className="logo-container">
                  {loadingLogos.has(selectedDesignerCounterparty.id) && (
                    <div className="logo-loading">Загрузка...</div>
                  )}
                  <img
                    src={getCompanyLogoUrl(
                      selectedDesignerCounterparty.logo_url,
                    )}
                    alt={getCounterpartyLabel(selectedDesignerCounterparty)}
                    onLoadStart={() => {
                      setLoadingLogos(
                        (prev) =>
                          new Set([...prev, selectedDesignerCounterparty.id]),
                      );
                    }}
                    onError={() => {
                      handleLogoError(selectedDesignerCounterparty.id);
                      setLoadingLogos((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(selectedDesignerCounterparty.id);
                        return newSet;
                      });
                    }}
                    onLoad={() => {
                      setLoadingLogos((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(selectedDesignerCounterparty.id);
                        return newSet;
                      });
                      setLogoErrors((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(selectedDesignerCounterparty.id);
                        return newSet;
                      });
                    }}
                  />
                </div>
              )}
          </div>

          <div className="sender-info-cyrillic">
            <div className="company-name">
              {getCounterpartyLabel(selectedDesignerCounterparty)}
            </div>
            <div className="company-address-ru">
              {formatCounterpartyAddress(selectedDesignerCounterparty)}
            </div>
            <div className="company-details">
              <div>БИН/ИИН: {selectedDesignerCounterparty?.bin_or_inn}</div>
              <div>тел: {selectedDesignerCounterparty?.phone}</div>
              <div>e-mail: {selectedDesignerCounterparty?.email}</div>
            </div>
          </div>
        </div>

        <div className="document-bottom">
          <div className="outgoing-info">
            <div className="outgoing-number">
              <strong>исх. №</strong>{" "}
              {commercialData?.outgoing_number || "_________________"}
            </div>
            <div className="outgoing-date">
              <strong>от</strong>{" "}
              {commercialData?.outgoing_date
                ? new Date(commercialData.outgoing_date).toLocaleDateString(
                    "ru-RU",
                  )
                : "_________________"}
            </div>
          </div>
          <div className="recipient-company">
            <strong>
              {selectedRecipientCounterparty
                ? getRecipientLabel(selectedRecipientCounterparty)
                : commercialData?.recipient_company ||
                  projectData?.recipient_company ||
                  ""}
            </strong>
          </div>
        </div>

        <div className="document-title">КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ</div>

        <div className="document-content">
          {/* Поле title — вводная часть под заголовком (редактируемое, с иконкой карандаша) */}
          <div
            className="document-title-block"
            style={{ position: "relative", marginBottom: "20px" }}
          >
            {isEditingTitle ? (
              <div className="document-editable-edit">
                <textarea
                  value={editingTitleValue}
                  onChange={(e) => setEditingTitleValue(e.target.value)}
                  placeholder="Настоящим выражаем свое почтение и признательность за возможность участвовать в проекте по строительству объекта:&#10;&#10;«Название проекта»&#10;&#10;На основании имеющихся данных, предлагаем разработать..."
                  rows={6}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "6px",
                    border: "1px solid #2e7d32",
                    resize: "vertical",
                  }}
                  autoFocus
                />
                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <button
                    type="button"
                    className="btn-save-offer"
                    style={{ padding: "6px 16px", fontSize: "13px" }}
                    onClick={async () => {
                      setOfferTitle(editingTitleValue);
                      setIsEditingTitle(false);
                      try {
                        setSaveOfferLoading(true);
                        const payload = {
                          title: editingTitleValue.trim() || null,
                          nds_percent: ndsRate,
                        };
                        if (selectedDesigner?.value) {
                          payload.counterparty_id = Number(
                            selectedDesigner.value,
                          );
                        }
                        if (selectedRecipient?.value) {
                          payload.receiver_id = Number(selectedRecipient.value);
                        }
                        const days =
                          workDeadlineDays !== ""
                            ? Number(workDeadlineDays)
                            : null;
                        if (days != null && !Number.isNaN(days) && days >= 0)
                          payload.deadline_days = days;
                        if (deadlineDescription.trim()) {
                          payload.deadline_description =
                            deadlineDescription.trim();
                        }
                        const updated = await updateSketchDesignCommercialOffer(
                          projectId,
                          payload,
                        );
                        setCommercialData(updated);
                        setOfferTitle(updated?.title ?? "");
                        if (updated?.current_nds != null)
                          setNdsRate(updated.current_nds);
                        setShowSaveSuccess(true);
                        setTimeout(() => setShowSaveSuccess(false), 3000);
                      } catch (err) {
                        console.error(err);
                        alert(err?.message || "Ошибка сохранения");
                      } finally {
                        setSaveOfferLoading(false);
                      }
                    }}
                    disabled={!isEditable || saveOfferLoading}
                  >
                    {saveOfferLoading ? "Сохранение…" : "Сохранить"}
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: "6px 16px",
                      fontSize: "13px",
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setIsEditingTitle(false);
                      setEditingTitleValue(
                        offerTitle || commercialData?.title || "",
                      );
                    }}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="document-editable-display text-center"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <div style={{ flex: 1, maxWidth: "800px" }}>
                  <p style={{ whiteSpace: "pre-line", margin: 0 }}>
                    {offerTitle ||
                      commercialData?.title ||
                      (() => {
                        const intro =
                          "Настоящим выражаем свое почтение и признательность за возможность участвовать в проекте по строительству объекта:";
                        const projectName =
                          commercialData?.project_name || projectData?.name;
                        const base = projectName
                          ? `${intro}\n\n«${projectName}»\n\nНа основании имеющихся данных, предлагаем разработать эскизный проект.`
                          : `${intro}\n\nНа основании имеющихся данных, предлагаем разработать эскизный проект.`;
                        return base;
                      })()}
                  </p>
                </div>
                {isEditable && (
                  <button
                    type="button"
                    className="edit-icon-btn"
                    style={{ color: "#2e7d32", flexShrink: 0 }}
                    onClick={() => {
                      const projectName =
                        commercialData?.project_name || projectData?.name;
                      const defaultText = projectName
                        ? `Настоящим выражаем свое почтение и признательность за возможность участвовать в проекте по строительству объекта:\n\n«${projectName}»\n\nНа основании имеющихся данных, предлагаем разработать эскизный проект.`
                        : "Настоящим выражаем свое почтение и признательность за возможность участвовать в проекте по строительству объекта:\n\nНа основании имеющихся данных, предлагаем разработать эскизный проект.";
                      setEditingTitleValue(
                        offerTitle || commercialData?.title || defaultText,
                      );
                      setIsEditingTitle(true);
                    }}
                    title="Редактировать"
                  >
                    ✎
                  </button>
                )}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "20px",
            }}
          >
            {commercialData?.order_number || projectData?.order_number ? (
              <div>
                <strong>
                  Заказ №
                  {commercialData?.order_number || projectData?.order_number}
                </strong>
              </div>
            ) : null}
            <div className="document-cost">
              <strong>
                за стоимость{" "}
                {selectedDesignerCounterparty?.is_nds_payer
                  ? commercialData?.final_offer_price?.toLocaleString(
                      "ru-RU",
                    ) || "0"
                  : commercialData?.final_offer_price_without_nds?.toLocaleString(
                      "ru-RU",
                    ) || "0"}{" "}
                тг{" "}
                {selectedDesignerCounterparty?.is_nds_payer
                  ? "с учетом НДС"
                  : "без учета НДС"}
              </strong>
            </div>
          </div>
        </div>

        {/* Таблица работ */}
        <div className="work-table">
          {commercialData && commercialData.blocks ? (
            (() => {
              const { hasSpots, groups } = groupBlocksBySpot(
                commercialData.blocks,
              );
              let rowIndex = 0;
              return (
                <table>
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Наименование</th>
                      <th>Площадь, м2</th>
                      <th>Стоимость, тг/м2</th>
                      <th>Коэффициент корректировки</th>
                      <th>ИТОГО</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasSpots ? (
                      <>
                        {groups.map((group, groupIdx) => {
                          const spotNumber =
                            group.spotId != null
                              ? groups
                                  .filter((g) => g.spotId != null)
                                  .indexOf(group) + 1
                              : 0;
                          const name = (group.spotName || "").trim();
                          const spotLabel =
                            group.spotId != null
                              ? name || `Пятно ${spotNumber}`
                              : "Без пятна";
                          const totalArea = group.blocks.reduce(
                            (s, b) => s + (b.area || 0),
                            0,
                          );
                          const totalPrice = group.blocks.reduce(
                            (s, b) =>
                              s + (b.final_block_price_without_nds || 0),
                            0,
                          );
                          return (
                            <React.Fragment key={group.spotId ?? "no-spot"}>
                              <tr className="work-table-spot-header">
                                <td colSpan="6">{spotLabel}</td>
                              </tr>
                              {group.blocks.map((block, i) => {
                                rowIndex++;
                                return (
                                  <tr
                                    key={`${group.spotId ?? "n"}-${i}`}
                                    className={
                                      rowIndex % 2 === 0
                                        ? "table-row"
                                        : "table-row-alt"
                                    }
                                  >
                                    <td>{rowIndex}.</td>
                                    <td>{block.name || ""}</td>
                                    <td>
                                      {block.area?.toLocaleString("ru-RU") ||
                                        "0"}
                                    </td>
                                    <td>
                                      {block.rate?.toLocaleString("ru-RU") ||
                                        "0"}
                                    </td>
                                    <td>
                                      {getBlockAttachmentCoeff(block)?.toFixed(
                                        2,
                                      ) || "1.00"}
                                    </td>
                                    <td>
                                      {block.final_block_price_without_nds?.toLocaleString(
                                        "ru-RU",
                                      ) || "0"}
                                    </td>
                                  </tr>
                                );
                              })}
                              {groups.length > 1 && (
                                <tr className="work-table-spot-total">
                                  <td></td>
                                  <td>Итого:</td>
                                  <td>{totalArea.toLocaleString("ru-RU")}</td>
                                  <td></td>
                                  <td></td>
                                  <td>
                                    {totalPrice.toLocaleString("ru-RU", {
                                      maximumFractionDigits: 0,
                                    })}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </>
                    ) : (
                      commercialData.blocks.map((block, index) => (
                        <tr
                          key={index}
                          className={
                            index % 2 === 0 ? "table-row" : "table-row-alt"
                          }
                        >
                          <td>{index + 1}.</td>
                          <td>{block.name || ""}</td>
                          <td>{block.area?.toLocaleString("ru-RU") || "0"}</td>
                          <td>{block.rate?.toLocaleString("ru-RU") || "0"}</td>
                          <td>
                            {getBlockAttachmentCoeff(block)?.toFixed(2) ||
                              "1.00"}
                          </td>
                          <td>
                            {block.final_block_price_without_nds?.toLocaleString(
                              "ru-RU",
                            ) || "0"}
                          </td>
                        </tr>
                      ))
                    )}
                    <tr className="grand-total">
                      <td></td>
                      <td>ИТОГО</td>
                      <td>
                        {commercialData.full_offer_area?.toLocaleString(
                          "ru-RU",
                        ) || "0"}
                      </td>
                      <td></td>
                      <td></td>
                      <td>
                        {commercialData.final_offer_price_without_nds?.toLocaleString(
                          "ru-RU",
                        ) || "0"}
                      </td>
                    </tr>
                    {selectedDesignerCounterparty?.is_nds_payer &&
                      (commercialData?.current_nds ?? ndsRate) > 0 && (
                        <>
                          <tr className="nds-row">
                            <td></td>
                            <td colSpan="4" style={{ textAlign: "right" }}>
                              НДС {commercialData?.current_nds ?? ndsRate}%
                            </td>
                            <td>
                              {(
                                (commercialData.final_offer_price || 0) -
                                (commercialData.final_offer_price_without_nds ||
                                  0)
                              ).toLocaleString("ru-RU", {
                                maximumFractionDigits: 0,
                              })}
                            </td>
                          </tr>
                          <tr className="grand-total-with-nds">
                            <td></td>
                            <td colSpan="4" style={{ textAlign: "right" }}>
                              Итого с НДС
                            </td>
                            <td>
                              {commercialData.final_offer_price?.toLocaleString(
                                "ru-RU",
                                {
                                  maximumFractionDigits: 0,
                                },
                              ) || "0"}
                            </td>
                          </tr>
                        </>
                      )}
                  </tbody>
                </table>
              );
            })()
          ) : (
            <div className="no-data">Нет данных для отображения</div>
          )}
        </div>

        {/* Таблица графика платежей */}
        <div
          className={`payment-table ${paymentProcedures.length === 0 ? "payment-table-empty" : ""}`}
        >
          <div
            className="payment-table-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <h3>Порядок оплаты</h3>
            <button
              type="button"
              className="btn-add-payment"
              onClick={() => {
                setAddPaymentForm({
                  stage: "",
                  percentage_ratio: "",
                  deadline: "",
                });
                setShowAddPaymentModal(true);
              }}
              disabled={
                !isEditable || paymentActionLoading || remainingPercent <= 0
              }
              title={
                remainingPercent <= 0
                  ? "Сумма процентов уже 100%"
                  : !isEditable
                    ? "Редактирование заблокировано"
                    : undefined
              }
            >
              {paymentProcedures.length === 0
                ? "Создать порядок оплаты"
                : "Добавить этап"}
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Этап</th>
                <th>В процентном соотношении</th>
                <th>Стоимость, тг</th>
                <th>Срок платежа</th>
                <th style={{ width: "90px" }}></th>
              </tr>
            </thead>
            <tbody>
              {paymentProcedures.length > 0 ? (
                <>
                  {paymentProcedures.map((payment, index) => (
                    <tr key={payment.id || index}>
                      <td>{index + 1}</td>
                      <td>{payment.stage}</td>
                      <td>
                        {editingPercentageId === payment.id ? (
                          (() => {
                            const otherTotal = paymentProcedures
                              .filter((p) => p.id !== payment.id)
                              .reduce(
                                (s, p) => s + (Number(p.percentage_ratio) || 0),
                                0,
                              );
                            const maxForThis = 100 - otherTotal;
                            return (
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <input
                                  type="number"
                                  min={0}
                                  max={maxForThis}
                                  value={editingPercentageValue}
                                  onChange={(e) =>
                                    setEditingPercentageValue(e.target.value)
                                  }
                                  style={{ width: "56px", padding: "4px 6px" }}
                                />
                                %
                                {maxForThis < 100 && (
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    {" "}
                                    (макс. {maxForThis})
                                  </span>
                                )}
                                <button
                                  type="button"
                                  disabled={!isEditable}
                                  onClick={async () => {
                                    if (!isEditable) return;
                                    const num = Number(editingPercentageValue);
                                    if (
                                      Number.isNaN(num) ||
                                      num < 0 ||
                                      num > maxForThis
                                    ) {
                                      alert(
                                        `Сумма процентов не должна превышать 100%. Для этого этапа доступно не более ${maxForThis}%.`,
                                      );
                                      return;
                                    }
                                    setPaymentActionLoading(true);
                                    try {
                                      await updateSketchDesignPaymentProcedure(
                                        projectId,
                                        {
                                          payment_procedure_id: payment.id,
                                          percentage_ratio: num,
                                        },
                                      );
                                      setEditingPercentageId(null);
                                      await refetchPaymentAndOffer();
                                    } catch (err) {
                                      console.error(err);
                                      alert(
                                        err?.message || "Ошибка обновления",
                                      );
                                    } finally {
                                      setPaymentActionLoading(false);
                                    }
                                  }}
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingPercentageId(null)}
                                >
                                  ✕
                                </button>
                              </span>
                            );
                          })()
                        ) : (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            {payment.percentage_ratio}%
                            {isEditable && (
                              <button
                                type="button"
                                className="edit-icon-btn"
                                onClick={() => {
                                  setEditingPercentageId(payment.id);
                                  setEditingPercentageValue(
                                    String(payment.percentage_ratio ?? ""),
                                  );
                                }}
                                title="Изменить процент"
                              >
                                ✎
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                      <td>{payment.cost?.toLocaleString("ru-RU") || "0"}</td>
                      <td>{payment.deadline}</td>
                      <td>
                        <button
                          type="button"
                          className="delete-payment-btn"
                          onClick={async () => {
                            if (
                              !isEditable ||
                              !window.confirm("Удалить этап оплаты?")
                            )
                              return;
                            setPaymentActionLoading(true);
                            try {
                              await deleteSketchDesignPaymentProcedure(
                                payment.id,
                              );
                              await refetchPaymentAndOffer();
                            } catch (err) {
                              console.error(err);
                              alert(err?.message || "Ошибка удаления");
                            } finally {
                              setPaymentActionLoading(false);
                            }
                          }}
                          disabled={!isEditable || paymentActionLoading}
                          title="Удалить этап"
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="grand-total">
                    <td colSpan="3">ИТОГО</td>
                    <td>
                      {paymentProcedures
                        .reduce((sum, payment) => sum + (payment.cost || 0), 0)
                        .toLocaleString("ru-RU")}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#666",
                    }}
                  >
                    Нет данных о порядке оплаты. Нажмите «Создать порядок
                    оплаты», чтобы добавить первый этап.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Модальное окно добавления этапа оплаты */}
        {showAddPaymentModal && (
          <div
            className="payment-modal-overlay"
            onClick={() => setShowAddPaymentModal(false)}
          >
            <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
              <h4>Добавить этап оплаты</h4>
              <div className="payment-modal-form">
                <label>
                  Этап
                  <input
                    type="text"
                    value={addPaymentForm.stage}
                    onChange={(e) =>
                      setAddPaymentForm((p) => ({
                        ...p,
                        stage: e.target.value,
                      }))
                    }
                    placeholder="Например: Аванс"
                  />
                </label>
                <label>
                  Процент, %{" "}
                  {remainingPercent < 100 && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginLeft: "8px",
                      }}
                    >
                      (доступно до {remainingPercent}%)
                    </span>
                  )}
                  <input
                    type="number"
                    min={0}
                    max={remainingPercent}
                    value={addPaymentForm.percentage_ratio}
                    onChange={(e) =>
                      setAddPaymentForm((p) => ({
                        ...p,
                        percentage_ratio: e.target.value,
                      }))
                    }
                    placeholder={
                      remainingPercent > 0 ? String(remainingPercent) : "0"
                    }
                  />
                </label>
                <label>
                  Срок платежа
                  <input
                    type="text"
                    value={addPaymentForm.deadline}
                    onChange={(e) =>
                      setAddPaymentForm((p) => ({
                        ...p,
                        deadline: e.target.value,
                      }))
                    }
                    placeholder="В течение 10 банковских дней..."
                  />
                </label>
              </div>
              <div className="payment-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowAddPaymentModal(false)}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    !isEditable ||
                    paymentActionLoading ||
                    !addPaymentForm.stage ||
                    !addPaymentForm.percentage_ratio ||
                    remainingPercent <= 0
                  }
                  onClick={async () => {
                    const num = Number(addPaymentForm.percentage_ratio);
                    if (Number.isNaN(num) || num < 0) {
                      alert("Введите неотрицательный процент");
                      return;
                    }
                    if (remainingPercent <= 0) {
                      alert(
                        "Сумма процентов уже 100%. Удалите или измените существующие этапы.",
                      );
                      return;
                    }
                    if (num > remainingPercent) {
                      alert(
                        `Сумма процентов не должна превышать 100%. Доступно не более ${remainingPercent}%.`,
                      );
                      return;
                    }
                    setPaymentActionLoading(true);
                    try {
                      await addSketchDesignPaymentProcedure(projectId, {
                        stage: addPaymentForm.stage.trim(),
                        percentage_ratio: num,
                        deadline: addPaymentForm.deadline.trim() || "",
                      });
                      setShowAddPaymentModal(false);
                      setAddPaymentForm({
                        stage: "",
                        percentage_ratio: "",
                        deadline: "",
                      });
                      await refetchPaymentAndOffer();
                    } catch (err) {
                      console.error(err);
                      alert(err?.message || "Ошибка добавления");
                    } finally {
                      setPaymentActionLoading(false);
                    }
                  }}
                >
                  {paymentActionLoading ? "Сохранение…" : "Добавить"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Срок выполнения работ с даты получения аванса — редактируемое поле с иконкой карандаша */}
        <div className="additional-info">
          <div className="info-section">
            {isEditingDeadline ? (
              <div
                className="document-editable-edit"
                style={{ marginBottom: "16px" }}
              >
                <strong>Срок выполнения работ с даты получения аванса: </strong>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                    marginTop: "8px",
                    alignItems: "center",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <input
                      type="number"
                      min={0}
                      value={workDeadlineDays}
                      onChange={(e) =>
                        setWorkDeadlineDays(
                          e.target.value === "" ? "" : e.target.value,
                        )
                      }
                      placeholder="дней"
                      style={{ width: "70px", padding: "6px" }}
                    />
                    <span>дней</span>
                  </label>
                  <textarea
                    value={editingDeadlineValue}
                    onChange={(e) => setEditingDeadlineValue(e.target.value)}
                    placeholder="Например: 2 месяца. Или: В течение 60 рабочих дней..."
                    rows={2}
                    style={{
                      flex: "1 1 280px",
                      minWidth: "200px",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid #2e7d32",
                      resize: "vertical",
                    }}
                  />
                </div>
                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                  <button
                    type="button"
                    className="btn-save-offer"
                    style={{ padding: "6px 16px", fontSize: "13px" }}
                    onClick={async () => {
                      setDeadlineDescription(editingDeadlineValue);
                      setIsEditingDeadline(false);
                      try {
                        setSaveOfferLoading(true);
                        const payload = {
                          deadline_description:
                            editingDeadlineValue.trim() || null,
                        };
                        if (selectedDesigner?.value) {
                          payload.counterparty_id = Number(
                            selectedDesigner.value,
                          );
                        }
                        if (selectedRecipient?.value) {
                          payload.receiver_id = Number(selectedRecipient.value);
                        }
                        if (offerTitle.trim()) {
                          payload.title = offerTitle.trim();
                        }
                        payload.nds_percent = ndsRate;
                        const days =
                          workDeadlineDays !== ""
                            ? Number(workDeadlineDays)
                            : null;
                        if (days != null && !Number.isNaN(days) && days >= 0)
                          payload.deadline_days = days;
                        const updated = await updateSketchDesignCommercialOffer(
                          projectId,
                          payload,
                        );
                        setCommercialData(updated);
                        setDeadlineDescription(
                          updated?.deadline_description ?? "",
                        );
                        if (updated?.deadline_days != null)
                          setWorkDeadlineDays(updated.deadline_days);
                        if (updated?.current_nds != null)
                          setNdsRate(updated.current_nds);
                        setShowSaveSuccess(true);
                        setTimeout(() => setShowSaveSuccess(false), 3000);
                      } catch (err) {
                        console.error(err);
                        alert(err?.message || "Ошибка сохранения");
                      } finally {
                        setSaveOfferLoading(false);
                      }
                    }}
                    disabled={!isEditable || saveOfferLoading}
                  >
                    {saveOfferLoading ? "Сохранение…" : "Сохранить"}
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: "6px 16px",
                      fontSize: "13px",
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setIsEditingDeadline(false);
                      setEditingDeadlineValue(
                        deadlineDescription ||
                          commercialData?.deadline_description ||
                          "",
                      );
                    }}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <p
                className={`work-deadline ${deadlineDisplayText === "—" ? "work-deadline-empty" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  margin: 0,
                }}
              >
                <span>
                  Срок выполнения работ с даты получения аванса:{" "}
                  {deadlineDisplayText}
                </span>
                {isEditable && (
                  <button
                    type="button"
                    className="edit-icon-btn"
                    style={{ color: "#2e7d32", flexShrink: 0 }}
                    onClick={() => {
                      setEditingDeadlineValue(
                        deadlineDescription ||
                          commercialData?.deadline_description ||
                          commercialData?.deadline ||
                          "",
                      );
                      setIsEditingDeadline(true);
                    }}
                    title="Редактировать"
                  >
                    ✎
                  </button>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="document-footer">
          {/* Реквизиты генпроектировщика: из API или из данных контрагента */}
          {selectedDesignerCounterparty && (
            <div className="info-section document-footer-requisites">
              <h4>
                Реквизиты "
                {getCounterpartyLabel(selectedDesignerCounterparty) || ""}":
              </h4>
              {commercialData?.requisites ? (
                <p>{commercialData.requisites}</p>
              ) : (
                <p style={{ whiteSpace: "pre-line" }}>
                  {[
                    formatCounterpartyAddress(selectedDesignerCounterparty),
                    selectedDesignerCounterparty.bin_or_inn &&
                      `БИН/ИИН: ${selectedDesignerCounterparty.bin_or_inn}`,
                    designerPrimaryBankAccount?.bank_name &&
                      `Банк: ${designerPrimaryBankAccount.bank_name}`,
                    designerPrimaryBankAccount?.bik &&
                      `БИК: ${designerPrimaryBankAccount.bik}`,
                    designerPrimaryBankAccount?.iik &&
                      `ИИК: ${designerPrimaryBankAccount.iik}`,
                    selectedDesignerCounterparty.phone &&
                      `тел.: ${selectedDesignerCounterparty.phone}`,
                    selectedDesignerCounterparty.email &&
                      `e-mail: ${selectedDesignerCounterparty.email}`,
                  ]
                    .filter(Boolean)
                    .join("\n")}
                </p>
              )}
            </div>
          )}

          <div className="document-footer-signatures">
            {/* С уважением, над подписью */}
            <div
              className="signatures-regards"
              style={{ marginBottom: "16px" }}
            >
              С уважением,
            </div>

            {/* Подписи: одна строка "Должность ____________ ФИО" */}
            <div className="signatures">
              <div className="signature-block">
                <div className="signature-images">
                  {selectedDesignerCounterparty?.signature_image_name && (
                    <div
                      className="signature-image-editable"
                      style={{
                        left: `${signatureLayout.signature.x}px`,
                        top: `${signatureLayout.signature.y}px`,
                        width: `${signatureLayout.signature.w}px`,
                        height: `${signatureLayout.signature.h}px`,
                      }}
                      onMouseDown={startSignatureDrag("signature", "move")}
                    >
                      <img
                        src={getImageServiceUrl(
                          selectedDesignerCounterparty.signature_image_name,
                        )}
                        alt="Подпись"
                        className="signature-image signature-image-signature"
                      />
                      {isEditable && (
                        <span
                          className="signature-resize-handle"
                          onMouseDown={startSignatureDrag(
                            "signature",
                            "resize",
                          )}
                        />
                      )}
                    </div>
                  )}
                  {selectedDesignerCounterparty?.seal_image_name && (
                    <div
                      className="signature-image-editable"
                      style={{
                        left: `${signatureLayout.seal.x}px`,
                        top: `${signatureLayout.seal.y}px`,
                        width: `${signatureLayout.seal.w}px`,
                        height: `${signatureLayout.seal.h}px`,
                      }}
                      onMouseDown={startSignatureDrag("seal", "move")}
                    >
                      <img
                        src={getImageServiceUrl(
                          selectedDesignerCounterparty.seal_image_name,
                        )}
                        alt="Печать"
                        className="signature-image signature-image-seal"
                      />
                      {isEditable && (
                        <span
                          className="signature-resize-handle"
                          onMouseDown={startSignatureDrag("seal", "resize")}
                        />
                      )}
                    </div>
                  )}
                </div>
                {[
                  commercialData?.signature_title ||
                    selectedDesignerCounterparty?.job_title ||
                    "",
                  signatureName
                    ? `____________ ${signatureName}`
                    : "____________",
                ]
                  .filter(Boolean)
                  .join(" ")}
              </div>
              {commercialData?.executor && (
                <div className="performer">
                  <div>Исполнитель - {commercialData.executor}</div>
                </div>
              )}
              <div className="date">
                от{" "}
                {commercialData?.date
                  ? new Date(commercialData.date).toLocaleDateString("ru-RU")
                  : new Date().toLocaleDateString("ru-RU")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Кнопки действий */}
      <div className="commercial-offer-actions">
        <button
          type="button"
          className="btn-print-pdf"
          onClick={handlePrintPdf}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M14 2H2V14H14V2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6H10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 9H10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 12H8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Печать PDF
        </button>
      </div>

      {/* Блок действий согласующего: Согласовать / Отклонить (если я в списке и статус «на рассмотрении») */}
      {isMyApprovalPending && (
        <div className="commercial-offer-approver-actions">
          <span className="commercial-offer-approver-actions__label">
            Ваше решение по согласованию:
          </span>
          <div className="commercial-offer-approver-actions__buttons">
            <button
              type="button"
              className="commercial-offer-approver-actions__btn commercial-offer-approver-actions__btn--approve"
              onClick={() => handleApproverSetStatus("approved")}
              disabled={isApproverActionLoading}
            >
              {isApproverActionLoading ? "..." : "Согласовать"}
            </button>
            <button
              type="button"
              className="commercial-offer-approver-actions__btn commercial-offer-approver-actions__btn--reject"
              onClick={() => handleApproverSetStatus("rejected")}
              disabled={isApproverActionLoading}
            >
              Отклонить
            </button>
          </div>
        </div>
      )}

      {/* Попап успешного сохранения (как в InitialData) */}
      {showSaveSuccess && (
        <div
          className="popup-overlay"
          onClick={() => setShowSaveSuccess(false)}
        >
          <div className="popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon" />
            <p className="popup-text">
              Данные коммерческого предложения сохранены
            </p>
          </div>
        </div>
      )}

      <CommercialOfferApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onSubmit={handleApprovalSubmit}
        isSubmitting={isSubmittingApproval}
        title="Согласование КП (ЭП)"
        warningText="Подтвердите отправку коммерческого предложения на согласование."
      />

      <AddCounterpartyModal
        open={showAddDesignerModal}
        onClose={() => setShowAddDesignerModal(false)}
        defaultType="organization"
        onCounterpartyCreated={(newCounterparty) => {
          const newOption = {
            value: normalizeId(newCounterparty.id),
            label: getCounterpartyLabel(newCounterparty),
          };
          setDesignersCounterparties((prev) => [...prev, newCounterparty]);
          setSelectedDesigner(newOption);
          if (selectedRecipient?.value) {
            handleSaveOfferParams({
              designerId: newCounterparty.id,
              recipientId: selectedRecipient.value,
            });
          }
        }}
      />

      <AddCounterpartyModal
        open={showAddRecipientModal}
        onClose={() => setShowAddRecipientModal(false)}
        defaultType="counterparty"
        onCounterpartyCreated={(newCounterparty) => {
          const newOption = {
            value: normalizeId(newCounterparty.id),
            label: getRecipientLabel(newCounterparty),
          };
          setRecipientsCounterparties((prev) => [...prev, newCounterparty]);
          setSelectedRecipient(newOption);
          if (selectedDesigner?.value) {
            handleSaveOfferParams({
              designerId: selectedDesigner.value,
              recipientId: newCounterparty.id,
            });
          }
        }}
      />
    </div>
  );
};

export default CommercialOffer;
